<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { Ref } from '@/helpers/Decorators';
import { KeyValuePair, Option } from '@/helpers/Interfaces';
import { Form } from '@/helpers/Form';
import { BpmnEmitOptions } from '@/modules/low-code/process/services/BpmnService';
import { FrontActionModal } from '@/components/common/dynamic-grid/helpers/GridEnums';
import ProcessService, { ChangeProcessModel } from '@/modules/low-code/process/services/ProcessService';
import IdeoModal from '@/components/ideo/modal/IdeoModal.vue';
import Pager from '@/helpers/Pager';

@Options({
    emits: ['reload'],
})
export default class ChangeProcessModal extends Vue
{
    @Ref('modal') public modal: () => InstanceType<typeof IdeoModal>;

    public form = Form.create<ChangeProcessModel>({
        versionId: null,
        stepId: null,
        statusId: null,
    });

    public optionsLoading: boolean = true;
    public documentId: string = null;
    public licence: string = null;

    public versionOptions: KeyValuePair[] = [];
    public stepOptions: KeyValuePair[] = [];
    public statusOptions: KeyValuePair[] = [];

    public async mounted(): Promise<void>
    {
        this.$events.$on(FrontActionModal.ChangeProcessModal, async (action: BpmnEmitOptions) =>
        {
            this.documentId = action.id;
            this.licence = action.licence;

            await this.getVersionOptions();
            await this.loadDetails();

            this.modal().show();
        });
    }

    public unmounted(): void
    {
        this.$events.$off(FrontActionModal.ChangeProcessModal);
    }

    public async accept(): Promise<void>
    {
        try
        {
            this.form.wait();

            await ProcessService.changeDocumentProcess(this.licence, this.documentId, this.form.formatData());
            this.$alert.success(this.$t('[[[Akcja wykonana pomyślnie]]]'));
            this.modal().hide();

            setTimeout(() =>
            {
                this.$emit('reload');
                this.$events.$emit('refetchActions');
                this.form.reset();
            }, 500);
            this.form.clear();
        }
        catch (ex)
        {
            this.handleException(ex, {
                400: (ex: any) => this.$alert.warning(ex.message),
                422: (ex: any) => [
                    this.form.$errors.record(ex.data.errors),
                    this.$alert.warning(this.$t('[[[Nie wszystkie pola zostały wypełnione prawidłowo.]]]')),
                ],
            });
        }
        finally
        {
            this.form.continue();
        }
    }

    public async getVersionOptions(): Promise<void>
    {
        try
        {
            const options = await ProcessService.getDocumentProcessVersions(this.licence, this.documentId, new Pager(0, 1000, "Id", "desc"));

            this.versionOptions = options.items.map(p => p.result);
        }
        catch (ex)
        {
            this.handleException(ex, {
                400: (ex: any) => this.$alert.warning(ex.message),
            });
        }
    }

    public async getStepOptions(): Promise<void>
    {
        if (!this.form.versionId)
        {
            this.stepOptions = [];

            return;
        }

        try
        {
            const options = await ProcessService.getProcessSteps(this.licence, this.documentId, (this.form.versionId as KeyValuePair).key, new Pager(0, 1000, "Value"));

            this.stepOptions = options.items.map(p => p.result);
        }
        catch (ex)
        {
            this.handleException(ex, {
                400: (ex: any) => this.$alert.warning(ex.message),
            });
        }
    }

    public async getStatusOptions(): Promise<void>
    {
        if (!this.form.versionId)
        {
            this.statusOptions = [];

            return;
        }

        try
        {
            const options = await ProcessService.getProcessStatuses(this.licence, this.documentId, (this.form.versionId as KeyValuePair).key, new Pager(0, 1000, "Value"));

            this.statusOptions = options.items.map(p => p.result);
        }
        catch (ex)
        {
            this.handleException(ex, {
                400: (ex: any) => this.$alert.warning(ex.message),
            });
        }
    }

    public async loadDetails(): Promise<boolean>
    {
        if (!this.documentId) return false;

        try
        {
            const processState = await ProcessService.getDocumentProcessState(this.licence, this.documentId);

            this.form.withData(processState.result);

            if (this.form.versionId)
            {
                await this.loadOptions();
            }
        }
        catch (ex)
        {
            this.handleException(ex, {
                400: (ex: any) => this.$alert.warning(ex.message),
            });
        }

        return true;
    }

    public async onVersionChanged(value: Option): Promise<void>
    {
        this.form.stepId = null;
        this.form.statusId = null;

        await this.loadOptions();
    }

    public async loadOptions(): Promise<void>
    {
        try
        {
            this.optionsLoading = true;

            await Promise.all([
                this.getStatusOptions(),
                this.getStepOptions(),
            ]);
        }
        catch (ex)
        {
            this.handleException(ex, {
                400: (e: any) => this.$alert.warning(e.message),
            });
        }
        finally
        {
            this.optionsLoading = false;
        }
    }
}
</script>

<template>
    <IdeoModal ref="modal" :title="$t('[[[Zmień wersję procesu]]]')" centered>
        <template #default>
            <IdeoAlert variant="warning" :show="true" class="mb-3">
                Wprowadzenie poniższych zmian w obiegu dokumentu może mieć wpływ na spójność danych przeliczonych na tym dokumencie lub dokumentach powiązanych.
            </IdeoAlert>
            <IdeoFormGroup
                :label="$t('[[[Wersja]]]')"
                :invalid-feedback="form.$errors.first('versionId')"
                :state="form.$errors.state('versionId')"
                required
            >
                <IdeoSelect
                    v-model="form.versionId"
                    :options="versionOptions"
                    @update:modelValue="onVersionChanged"
                />
            </IdeoFormGroup>
            <IdeoFormGroup
                v-if="form.versionId"
                :label="$t('[[[Krok]]]')"
                :invalid-feedback="form.$errors.first('stepId')"
                :state="form.$errors.state('stepId')"
                required
            >
                <IdeoSelect
                    v-model="form.stepId"
                    :options="stepOptions"
                    :disabled="optionsLoading"
                />
            </IdeoFormGroup>
            <IdeoFormGroup
                v-if="form.versionId"
                :label="$t('[[[Status]]]')"
                :invalid-feedback="form.$errors.first('stepId')"
                :state="form.$errors.state('stepId')"
                required
            >
                <IdeoSelect
                    v-model="form.statusId"
                    :options="statusOptions"
                    :disabled="optionsLoading"
                />
            </IdeoFormGroup>
        </template>
        <template #modal-footer="{ cancel }">
            <ActionButton
                name="btnConfirmAccept"
                variant="success"
                :text="$t('[[[Zapisz]]]')"
                :confirm="$t('[[[Wprowadzenie  zmian może spowodować utratę spójności jeśli dane z tego dokumentu lub dokumentów powiązanych są przeliczane w trakcie procesowania. Czy na pewno wprowadzić zmiany?]]]')"
                @click="accept"
            >
            </ActionButton>
            <IdeoButton variant="secondary" @click="cancel">
                {{ $t('[[[Anuluj]]]') }}
            </IdeoButton>
        </template>
    </IdeoModal>
</template>

